import React, { useCallback, useMemo, useRef, useState } from 'react';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import TableHead from '@material-ui/core/TableHead';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TablePagination from '@material-ui/core/TablePagination';
import _ from 'lodash';

import { FROZEN_CELL_Z_INDEX, PINNED_HEADER_Z_INDEX } from './simple-table.constants';
import { countColumns, resolveColumnMinWidths } from './simple-table.helpers';
import { ColumnMinWidths } from './simple-table.types';

export interface ICell {
  borderLeft?: boolean;
  borderRight?: boolean;
  content?: string | React.ReactNode;
  colSpan?: number;
}

export interface IRow {
  cells: ICell[];
}


export interface ISimpleTableProps {
  headerRows: IRow[];
  rows: IRow[];
  highlightedRows?: number[];
  // Fills the container width (table-layout: fixed) and wraps cell content instead of
  // overflowing horizontally — for wide matrices whose columns can share width evenly.
  fitContainer?: boolean;
  // Floors each column's width (a <colgroup> under table-layout: fixed); a table wider than its
  // container then scrolls sideways in its own wrapper. `true` uses the defaults, `{ first, rest }` overrides them.
  columnMinWidths?: ColumnMinWidths;
  // Pins the header rows to the top of the page while any part of the table is in view, sideways
  // scroll included. Implies column floors, so the pinned copy of the header lines up with the body.
  stickyHeader?: boolean;
  // Keeps the first cell of every row, header rows included, in view while the table scrolls sideways.
  stickyFirstColumn?: boolean;
  pagination?: {
    enabled: boolean;
    rowsPerPageOptions?: number[];
    defaultRowsPerPage?: number;
  };
}

export const SimpleTable = ({headerRows, rows, highlightedRows, pagination, fitContainer, columnMinWidths, stickyHeader, stickyFirstColumn}: ISimpleTableProps) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(pagination?.defaultRowsPerPage || 10);
  const pinnedHeaderRef = useRef<HTMLDivElement>(null);
  const minWidths = useMemo(
    () => resolveColumnMinWidths(columnMinWidths || stickyHeader, countColumns(headerRows)),
    [columnMinWidths, stickyHeader, headerRows]
  );
  // Not memoized: a sum over a handful of numbers, not fed to any hook dep array.
  const tableMinWidth = minWidths ? _.sum(minWidths) : undefined;

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // The pinned header copy sits outside the body's scroller, so it follows that scroll by hand.
  const handleBodyScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
    if (pinnedHeaderRef.current) pinnedHeaderRef.current.scrollLeft = event.currentTarget.scrollLeft;
  }, []);

  // Calculate paginated rows
  const paginatedRows = pagination?.enabled
    ? rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    : rows;
  const fitStyles: React.CSSProperties = fitContainer ? { wordBreak: 'break-word', whiteSpace: 'normal' } : {};
  // Separate borders keep the header the same height with or without body rows beneath it (so the
  // pinned copy covers the real header exactly) and let a frozen cell carry its dividers along.
  const tableStyles: React.CSSProperties = {
    ...(fitContainer || minWidths ? { tableLayout: 'fixed', width: '100%' } : {}),
    ...(stickyHeader || stickyFirstColumn ? { borderCollapse: 'separate' } : {}),
    minWidth: tableMinWidth,
  };
  // A frozen cell paints above the cells scrolling under it, so it needs its own opaque background.
  const frozenCellStyles = (colIndex: number, background?: string): React.CSSProperties =>
    stickyFirstColumn && colIndex === 0
      ? { position: 'sticky', left: 0, zIndex: FROZEN_CELL_Z_INDEX, ...(background ? { backgroundColor: background } : {}) }
      : {};
  const columnGroup = minWidths && (
    <colgroup>
      {minWidths.map((width, index) => <col key={"col-" + index} style={{ width }} />)}
    </colgroup>
  );
  const tableHead = (
    <TableHead>
      {headerRows.map((row, index) => (
        <TableRow key={"header-" + index}>
          {row.cells.map(({content, colSpan, borderLeft, borderRight}, colIndex) => (
            <TableCell
              key={"header-" + index + "-cell-" + colIndex}
              colSpan={colSpan}
              style={{
                fontWeight: "bold",
                color: "white",
                backgroundColor: "#999",
                textAlign: "center",
                borderLeft: borderLeft ? "1px solid #ccc" : "none",
                borderRight: borderRight ? "1px solid #ccc" : "none",
                ...fitStyles,
                ...frozenCellStyles(colIndex)
              }}
            >
              {content}
            </TableCell>
          ))}
        </TableRow>
      ))}
    </TableHead>
  );

  return (
    // Both children share one grid cell, so the pinned copy's sticky travel is the table's height and
    // it slides out with the table's end; isolation keeps its z-index below page-level menus.
    <div style={{ display: 'grid', isolation: 'isolate' }}>
      {stickyHeader && (
        <div
          ref={pinnedHeaderRef}
          aria-hidden
          style={{ gridArea: '1 / 1', alignSelf: 'start', position: 'sticky', top: 0, zIndex: PINNED_HEADER_Z_INDEX, overflow: 'hidden' }}
        >
          <Table style={tableStyles}>
            {columnGroup}
            {tableHead}
          </Table>
        </div>
      )}
      <div style={{ gridArea: '1 / 1', overflowX: 'auto' }} onScroll={handleBodyScroll}>
        <Table style={tableStyles}>
          {columnGroup}
          {tableHead}
          <TableBody>
            {_.map(paginatedRows, (row, index) => {
              const actualIndex = pagination?.enabled ? (page * rowsPerPage + index) : index;
              const rowBackground = actualIndex % 2 === 0 ? "white" : "#eee";
              return (
                <TableRow key={"body-" + actualIndex} style={{ backgroundColor: rowBackground }}>
                  {row.cells.map(({content = "", colSpan, borderLeft, borderRight}, colIndex) => (
                    <TableCell
                      key={"body-" + index + "-cell-" + colIndex}
                      colSpan={colSpan}
                      style={{
                        fontWeight: highlightedRows?.includes(actualIndex) ? "bold" : "normal",
                        textAlign: "center",
                        borderLeft: borderLeft ? "1px solid #000" : "none",
                        borderRight: borderRight ? "1px solid #000" : "none",
                        ...fitStyles,
                        ...frozenCellStyles(colIndex, rowBackground)
                      }}
                    >
                      {content}
                    </TableCell>
                  ))}
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </div>
      {pagination?.enabled && (
        // Outside the scroller, so the page controls stay in reach however wide the table is.
        <TablePagination
          component="div"
          rowsPerPageOptions={pagination.rowsPerPageOptions || [5, 10, 25]}
          count={rows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          SelectProps={{
            inputProps: { 'aria-label': 'rows per page' },
            native: true,
          }}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      )}
    </div>
  )
}
